import React, { useEffect, useMemo, useRef, useState } from 'react';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import ConfigurationListPaginationFooter from '../../../components/AdminDashboard/Configuration/ConfigurationListPaginationFooter';
import { CONFIG_LIST_PAGE_SIZE } from '../../../utils/configurationTableUx';
import {
  AdminTableHeader,
  AdminTableBody,
  AdminTableMeta,
  AdminStatusBadge,
  AdminSupportingText,
  AdminRowActionsButton,
  AdminRowMenuItem,
  AdminRowMenuIcon,
  ADMIN_INLINE_ROW_ACTIONS_MENU_CLASS,
  ADMIN_TABLE_CLASS,
} from '../shared/adminUx';
import {
  getTransactionsForBusiness,
  formatMonthlyAmount,
  formatTransactionPaymentMethod,
  formatTransactionBillingPeriod,
  formatTransactionDateOnly,
} from './businessBillingTransactionsData';

/** Hosted Stripe receipt URL, or Dashboard payment page when only a pi_ provider ref is known. */
const getStripeReceiptHref = (txn) => {
  if (!txn) return null;
  if (txn.stripeReceiptUrl) return txn.stripeReceiptUrl;
  const pi = txn.providerReference;
  if (!pi || typeof pi !== 'string' || !pi.startsWith('pi_')) return null;
  return `https://dashboard.stripe.com/payments/${pi}`;
};

/**
 * Business Billing Record — Transactions tab.
 */
const BusinessBillingTransactionsTab = ({ businessId }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [rowActionsOpen, setRowActionsOpen] = useState({});
  const rowActionsRefs = useRef({});

  const allTransactions = useMemo(() => getTransactionsForBusiness(businessId), [businessId]);

  useEffect(() => {
    setCurrentPage(1);
    setRowActionsOpen({});
  }, [businessId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutsideAll = Object.values(rowActionsRefs.current).every(
        (ref) => ref && !ref.contains(event.target)
      );
      if (isOutsideAll) setRowActionsOpen({});
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const itemsPerPage = CONFIG_LIST_PAGE_SIZE;
  const totalPages = Math.max(1, Math.ceil(allTransactions.length / itemsPerPage));

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [allTransactions.length, currentPage, totalPages]);

  const paginatedTransactions = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return allTransactions.slice(start, start + itemsPerPage);
  }, [allTransactions, currentPage, itemsPerPage]);

  const rangeStart = allTransactions.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const rangeEnd = Math.min(currentPage * itemsPerPage, allTransactions.length);
  const showPagination = allTransactions.length > itemsPerPage;

  const toggleRowActions = (rowKey) => {
    setRowActionsOpen((prev) => {
      if (prev[rowKey]) return {};
      return { [rowKey]: true };
    });
  };

  const openStripeReceipt = (txn) => {
    const href = getStripeReceiptHref(txn);
    if (href) {
      window.open(href, '_blank', 'noopener,noreferrer');
    }
    setRowActionsOpen({});
  };

  // Transaction ID | Type | Plan | Amount | Status | Payment Method |
  // Transaction Date | Billing Period | Actions
  const gridTemplateColumns =
    'minmax(110px, 1fr) minmax(140px, 1.3fr) minmax(56px, 0.55fr) minmax(80px, 0.7fr) minmax(110px, 1fr) minmax(120px, 1.05fr) minmax(100px, 0.9fr) minmax(130px, 1.15fr) minmax(52px, auto)';

  return (
    <div>
      <DashboardMainContainer className={`${ADMIN_TABLE_CLASS} !mt-0`}>
        {allTransactions.length === 0 ? (
          <div className="text-center py-10">
            <AdminSupportingText className="text-mediumgray">
              No transactions for this account.
            </AdminSupportingText>
          </div>
        ) : (
          <>
            <div
              className="grid py-3 text-offblack border-b border-lightgray px-4 gap-4 items-center"
              style={{ gridTemplateColumns, width: '100%' }}
            >
              <AdminTableHeader>Transaction ID</AdminTableHeader>
              <AdminTableHeader>Type</AdminTableHeader>
              <AdminTableHeader>Plan</AdminTableHeader>
              <AdminTableHeader>Amount</AdminTableHeader>
              <AdminTableHeader>Status</AdminTableHeader>
              <AdminTableHeader>Payment Method</AdminTableHeader>
              <AdminTableHeader>Transaction Date</AdminTableHeader>
              <AdminTableHeader>Billing Period</AdminTableHeader>
              <div className="flex items-center justify-center min-h-8">
                <AdminTableHeader>Actions</AdminTableHeader>
              </div>
            </div>

            {paginatedTransactions.map((txn) => {
              const receiptHref = getStripeReceiptHref(txn);
              return (
                <div
                  key={txn.id}
                  className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-4 items-center"
                  style={{ gridTemplateColumns, width: '100%' }}
                >
                  <div className="flex items-center min-w-0 min-h-8">
                    <AdminTableBody className="truncate">{txn.id}</AdminTableBody>
                  </div>
                  <div className="flex items-center min-w-0 min-h-8">
                    <AdminTableBody className="truncate">{txn.type}</AdminTableBody>
                  </div>
                  <div className="flex items-center min-h-8">
                    <AdminTableBody>{txn.plan}</AdminTableBody>
                  </div>
                  <div className="flex items-center min-h-8">
                    <AdminTableBody className="tabular-nums">
                      {formatMonthlyAmount(txn.amount)}
                    </AdminTableBody>
                  </div>
                  <div className="flex items-center min-h-8">
                    <AdminStatusBadge status={txn.status} label={txn.status} />
                  </div>
                  <div className="flex items-center min-w-0 min-h-8">
                    <AdminTableBody className="truncate">
                      {formatTransactionPaymentMethod(txn)}
                    </AdminTableBody>
                  </div>
                  <div className="flex items-center min-h-8">
                    <AdminTableMeta>
                      {formatTransactionDateOnly(txn.transactionDate)}
                    </AdminTableMeta>
                  </div>
                  <div className="flex items-center min-w-0 min-h-8">
                    <AdminTableMeta className="truncate">
                      {formatTransactionBillingPeriod(txn)}
                    </AdminTableMeta>
                  </div>
                  <div className="flex items-center justify-center relative min-h-8">
                    <div
                      className="relative inline-flex"
                      ref={(el) => {
                        rowActionsRefs.current[txn.id] = el;
                      }}
                    >
                      <AdminRowActionsButton
                        onClick={() => toggleRowActions(txn.id)}
                        title="Actions"
                        aria-label="Row actions"
                      >
                        <AdminRowMenuIcon />
                      </AdminRowActionsButton>
                      {rowActionsOpen[txn.id] && (
                        <div className={ADMIN_INLINE_ROW_ACTIONS_MENU_CLASS}>
                          <AdminRowMenuItem
                            onClick={() => openStripeReceipt(txn)}
                            disabled={!receiptHref}
                          >
                            View Receipt
                          </AdminRowMenuItem>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </DashboardMainContainer>

      <ConfigurationListPaginationFooter
        show={showPagination}
        rangeStart={rangeStart}
        rangeEnd={rangeEnd}
        totalCount={allTransactions.length}
        currentPage={currentPage}
        totalPages={totalPages}
        onPrev={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
        onNext={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
        goToPage={setCurrentPage}
      />
    </div>
  );
};

export default BusinessBillingTransactionsTab;
